import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ClientDiagnosticsService,
  I18N,
  O3ToastService,
  TranslatePipe,
} from 'O3_core';
import { firstValueFrom } from 'rxjs';
import { TicketService } from '../services/ticket.service';
import {
  IRedactionRecord,
  TicketAttachmentService,
} from '../services/ticket-attachment.service';
import {
  ScreenshotCaptureError,
  ScreenshotCaptureService,
} from './screenshot-capture.service';
import {
  IRedactedImage,
  ScreenshotRedactor,
} from './screenshot-redactor/screenshot-redactor';
import {
  ITicketCategory,
  TicketCategoryCode,
} from '../models/ticket.interface';
import { TicketCategoryService } from '../services/ticket-catalog.service';
import enTranslations from './locales/en/resources.json';
import roTranslations from './locales/ro/resources.json';

/**
 * The 30-second capture surface.
 *
 * A right-hand **slide-over**, not a modal, and deliberately so: the reporter
 * is describing the screen behind it, so that screen must stay visible while
 * they type. A modal with a backdrop would hide the very thing being reported.
 *
 * Three inputs only — description, category, title. Everything technical
 * (correlation ids of recent failures, route, app version, browser, roles,
 * tenant, company/location) is attached automatically from
 * `ClientDiagnosticsService` at submit time, which is what makes 30 seconds
 * achievable without the reporter knowing any of it.
 */
@Component({
  selector: 'o3-support-ticket-capture-widget',
  imports: [CommonModule, FormsModule, TranslatePipe, ScreenshotRedactor],
  templateUrl: './ticket-capture-widget.html',
  styleUrl: './ticket-capture-widget.scss',
})
export class TicketCaptureWidget {
  private readonly ticketService = inject(TicketService);
  private readonly attachmentService = inject(TicketAttachmentService);
  private readonly screenshotCapture = inject(ScreenshotCaptureService);
  private readonly diagnostics = inject(ClientDiagnosticsService);
  private readonly toastService = inject(O3ToastService);
  private readonly elementRef = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18N);

  private readonly NS = 'Support_TicketCapture';

  constructor() {
    // Registered in the constructor, not via `i18nResolver`: the widget is
    // mounted globally in the layout rather than reached through a route, so
    // no route resolver ever runs for it.
    this.i18n.addResourceBundle('en', this.NS, enTranslations, true, true);
    this.i18n.addResourceBundle('ro', this.NS, roTranslations, true, true);

    // Fetched when the widget is first opened rather than at app start: this
    // is mounted globally in the layout, and most sessions never file a
    // ticket. The service caches, so reopening costs nothing.
    effect(() => {
      if (this.isOpen() && !this.categories().length) void this.loadCategories();
      // Closing discards the draft, so a later reopen starts expanded rather
      // than restoring a collapsed panel the user cannot see.
      if (!this.isOpen()) this.minimized.set(false);
    });
  }

  private async loadCategories(): Promise<void> {
    try {
      this.categories.set(await firstValueFrom(this.categoryService.all()));
      if (!this.categoryId()) this.categoryId.set(this.defaultCategoryId());
    } catch {
      // Silent: the widget stays usable for a retry, and a toast about a
      // catalog the user never asked for would be noise on top of whatever
      // they were already trying to report.
    }
  }

  isOpen = input.required<boolean>();
  closed = output<void>();

  private readonly categoryService = inject(TicketCategoryService);

  /**
   * Loaded from the catalog rather than hard-coded, so a category added in the
   * database appears here without a release. `bug` is preselected because it
   * is what most reports are; `TicketCategoryCode` is used only to find that
   * default row, never to label a button.
   */
  categories = signal<ITicketCategory[]>([]);

  /**
   * Collapsed to a corner button, with the draft intact.
   *
   * The panel covers the right-hand 420px of the screen, which is exactly the
   * area a reporter often needs to photograph. Closing the widget to get it
   * out of the way would throw away everything they had typed, so this hides
   * the panel without unmounting it: the signals below keep their values and
   * the screenshot they take can be attached on the way back.
   */
  minimized = signal(false);

  title = signal('');
  description = signal('');
  categoryId = signal('');
  submitting = signal(false);

  /**
   * Held until the ticket exists — the upload endpoint keys off a ticket id,
   * so the file is sent immediately after the ticket is created rather than
   * alongside it.
   */
  pendingFile = signal<File | null>(null);
  attachmentError = signal<string | null>(null);

  /**
   * What the reporter did in the redactor, carried to the upload.
   *
   * Every attached image has been through it — a capture and a file picked
   * from disk both go the same way — so this is always set alongside
   * `pendingFile` and never guessed at upload time.
   */
  pendingRedaction = signal<IRedactionRecord | null>(null);

  /** The image awaiting redaction. Nothing else can become `pendingFile`. */
  redactorSource = signal<Blob | null>(null);
  redactorFileName = signal('screenshot.png');

  capturing = signal(false);

  /**
   * Hides the panel for the duration of a capture.
   *
   * Not `minimized`: that is the reporter's own choice and survives, while
   * this is a transient state the widget puts itself into so the screenshot
   * shows the screen being reported rather than the form describing it.
   */
  private hiddenForCapture = signal(false);

  panelHidden = computed(() => this.minimized() || this.hiddenForCapture());

  /** Hidden rather than offered-and-failing where the browser cannot do it. */
  captureSupported = this.screenshotCapture.isSupported();

  /** What the reporter is about to send, shown before they send it. */
  contextPreview = computed(() => {
    const snapshot = this.diagnostics.snapshot();
    return {
      routePath: snapshot.routePath,
      failedRequestCount: snapshot.failedRequests.length,
      correlationIds: snapshot.correlationIds,
      appVersion: snapshot.appVersion,
    };
  });

  canSubmit = computed(
    () =>
      this.title().trim().length > 0 &&
      this.description().trim().length > 0 &&
      // The category is a foreign key now, so there is nothing to submit until
      // the catalog has loaded.
      this.categoryId().length > 0 &&
      !this.submitting(),
  );

  minimize(): void {
    if (this.submitting()) return;
    this.minimized.set(true);
  }

  restore(): void {
    this.minimized.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    // Ignored while minimized: the panel is not on screen to be dismissed,
    // and Escape during a screenshot must not discard the draft.
    if (this.minimized()) return;
    if (this.isOpen() && !this.submitting()) this.closed.emit();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen() || this.submitting()) return;
    // Every click is an outside click while minimized — that is the point of
    // minimizing. Without this guard the first click on the page being
    // photographed would close the widget and lose everything typed so far.
    if (this.minimized()) return;

    const target = event.target as HTMLElement;
    if (this.elementRef.nativeElement.contains(target)) return;
    // The topbar trigger toggles the widget itself; treating its click as an
    // outside-click would close and immediately reopen the panel.
    if (target.closest?.('[data-support-capture-toggle]')) return;

    this.closed.emit();
  }

  async submit(): Promise<void> {
    if (!this.canSubmit()) return;

    this.submitting.set(true);
    try {
      const ticket = await firstValueFrom(
        this.ticketService.fileTicket({
          title: this.title().trim(),
          description: this.description().trim(),
          categoryId: this.categoryId(),
          context: this.diagnostics.snapshot(),
        }),
      );

      // Best-effort: a failed attachment must not lose the ticket the user
      // just wrote, so it is reported separately rather than failing the
      // whole submission.
      const file = this.pendingFile();
      if (file) {
        try {
          await firstValueFrom(
            this.attachmentService.uploadToMyTicket(
              file,
              ticket.id,
              undefined,
              this.pendingRedaction() ?? undefined,
            ),
          );
        } catch {
          this.toastService.show({
            title: this.i18n.t(`${this.NS}:attachmentFailed`),
            content: this.i18n.t(`${this.NS}:attachmentFailedDetail`, {
              publicRef: ticket.publicRef,
            }),
            type: 'Warning',
          });
        }
      }

      this.toastService.show({
        title: this.i18n.t(`${this.NS}:submitted`),
        content: this.i18n.t(`${this.NS}:submittedDetail`, {
          publicRef: ticket.publicRef,
        }),
        type: 'Success',
      });
      this.resetForm();
    } catch {
      this.toastService.show({
        title: this.i18n.t(`${this.NS}:submitFailed`),
        content: this.i18n.t(`${this.NS}:submitFailedDetail`),
        type: 'Error',
      });
    } finally {
      this.submitting.set(false);
    }
  }

  onViewMyTickets(): void {
    this.closed.emit();
    void this.router.navigate(['/support', 'my-tickets']);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    this.attachmentError.set(null);

    if (!file) {
      this.pendingFile.set(null);
      return;
    }

    const problem = TicketAttachmentService.validate(file);
    if (problem) {
      this.attachmentError.set(problem);
      this.pendingFile.set(null);
      input.value = '';
      return;
    }

    // A PNG from the snipping tool holds exactly as much patient data as one
    // we captured, so it goes through the same step. "Every image attached to
    // a ticket was redacted" is then simply true, with no exception to
    // remember.
    this.openRedactor(file, file.name);
    input.value = '';
  }

  /**
   * Renders the page behind the widget and hands the result to the redactor.
   *
   * No permission prompt and no OS picker: the DOM is drawn into a canvas
   * locally. A clinician mid-consultation will not grant a screen-share
   * prompt, and a prompt offering "entire screen" is one that can capture the
   * other patient's chart on the second monitor.
   */
  async onTakeScreenshot(): Promise<void> {
    if (this.capturing()) return;

    this.attachmentError.set(null);
    this.capturing.set(true);
    this.hiddenForCapture.set(true);

    try {
      const capture = await this.screenshotCapture.capture(this.captureExclusions());
      this.openRedactor(capture.blob, 'screenshot.png');
    } catch (error) {
      // Every failure names itself, because the recovery is the same in each
      // case — attach a file instead — and a reporter who is told why will do
      // that rather than press the button again.
      this.attachmentError.set(
        error instanceof ScreenshotCaptureError ? error.reason : 'captureFailed',
      );
    } finally {
      this.hiddenForCapture.set(false);
      this.capturing.set(false);
    }
  }

  onRedacted(redacted: IRedactedImage): void {
    const problem = TicketAttachmentService.validate(redacted.file);
    if (problem) {
      // A redacted PNG can be marginally larger than its source, so the cap is
      // re-checked on what is actually going to be sent.
      this.attachmentError.set(problem);
      this.closeRedactor();
      return;
    }

    this.pendingFile.set(redacted.file);
    this.pendingRedaction.set({
      applied: true,
      count: redacted.redactionCount,
    });
    this.closeRedactor();
  }

  onRedactorCancelled(): void {
    this.closeRedactor();
  }

  clearFile(): void {
    this.pendingFile.set(null);
    this.pendingRedaction.set(null);
    this.attachmentError.set(null);
  }

  private openRedactor(source: Blob, fileName: string): void {
    this.redactorFileName.set(fileName);
    this.redactorSource.set(source);
  }

  private closeRedactor(): void {
    this.redactorSource.set(null);
  }

  /**
   * Everything that belongs to the reporting tool rather than to the screen
   * being reported: this widget, and any toast sitting over it.
   */
  private captureExclusions(): HTMLElement[] {
    const own = this.elementRef.nativeElement as HTMLElement;
    const toasts = Array.from(
      document.querySelectorAll<HTMLElement>('.e-toast-container'),
    );
    return [own, ...toasts];
  }

  /** The `bug` row when the catalog has one, otherwise simply the first. */
  private defaultCategoryId(): string {
    const rows = this.categories();
    const bug = rows.find((row) => row.code === TicketCategoryCode.Bug);
    return (bug ?? rows[0])?.id ?? '';
  }

  private resetForm(): void {
    this.minimized.set(false);
    this.title.set('');
    this.description.set('');
    this.categoryId.set(this.defaultCategoryId());
    this.pendingFile.set(null);
    this.pendingRedaction.set(null);
    this.redactorSource.set(null);
    this.attachmentError.set(null);
  }
}
